import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Link2, Loader2, DownloadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { formatDate } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const BANK_LABELS = {
  access_bank: 'Access Bank',
  zenith_bank: 'Zenith Bank',
  others: 'Other Bank'
};

const REASON_LABELS = {
  no_customer_match: 'No matching customer',
  no_account_row: 'No account record for this date'
};

export const UnmatchedDeposits = () => {
  const { formatCurrency } = useCurrency();
  const [items, setItems] = useState([]);
  const [cutoff, setCutoff] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [selections, setSelections] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [unmatchedRes, custRes] = await Promise.all([
        axios.get(`${API}/reconciliation-unmatched-deposits`, getAuthHeader()),
        axios.get(`${API}/customers`, getAuthHeader())
      ]);
      setItems(unmatchedRes.data.items || []);
      setCutoff(unmatchedRes.data.cutoff || '');
      setCustomers((custRes.data || []).filter(c => c.is_active !== false));
    } catch (e) {
      // Non-admins may not have access; fail quietly but surface genuine errors in the console
      if (e.response?.status && e.response.status !== 403) {
        console.error('Failed to load unmatched deposits', e);
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const keyFor = (it) => `${it.entry_id}_${it.bank}`;

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await axios.post(`${API}/reconciliation-sync-all-deposits`, {}, getAuthHeader());
      const { dates_processed, unmatched_remaining } = res.data;
      toast.success(`Synced deposits across ${dates_processed} day${dates_processed === 1 ? '' : 's'}. ${unmatched_remaining} still unmatched.`);
      window.dispatchEvent(new Event('unmatchedDepositsChanged'));
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sync deposits');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleAssign = async (it) => {
    const customerId = selections[keyFor(it)] || it.matched_customer_id;
    if (!customerId) {
      toast.error('Please select a customer first');
      return;
    }
    setAssigning(keyFor(it));
    try {
      await axios.post(`${API}/reconciliation-assign-deposit`, {
        entry_id: it.entry_id,
        bank: it.bank,
        customer_id: customerId
      }, getAuthHeader());
      toast.success('Deposit assigned and payment recorded');
      window.dispatchEvent(new Event('unmatchedDepositsChanged'));
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign deposit');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-lg border border-amber-200 shadow-sm" data-testid="unmatched-deposits-panel">
      <div className="flex items-center justify-between p-4 border-b border-amber-100 bg-amber-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="font-semibold text-slate-800">Unmatched Bank Deposits</h3>
            <p className="text-xs text-slate-500">
              Customer payments that couldn't be auto-recorded{cutoff ? ` (from ${formatDate(cutoff)} onward)` : ''}. Assign them to record in Customer Account Records.
            </p>
          </div>
        </div>
        <div className="flex items-center">
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-unmatched-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={handleSyncAll} disabled={syncingAll} className="bg-sky-600 hover:bg-sky-700 ml-2" data-testid="sync-all-deposits-btn">
            {syncingAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DownloadCloud className="w-4 h-4 mr-1" />}
            Sync All Deposits
          </Button>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-unmatched-msg">
            All deposits are matched and recorded. 🎉
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b text-slate-600">
                  <th className="p-2 font-semibold">Date</th>
                  <th className="p-2 font-semibold">Bank</th>
                  <th className="p-2 font-semibold">Description</th>
                  <th className="p-2 font-semibold text-right">Amount</th>
                  <th className="p-2 font-semibold">Issue</th>
                  <th className="p-2 font-semibold">Assign to Customer</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const k = keyFor(it);
                  return (
                    <tr key={k} className="border-b hover:bg-slate-50" data-testid={`unmatched-row-${k}`}>
                      <td className="p-2 whitespace-nowrap">{formatDate(it.date)}</td>
                      <td className="p-2 whitespace-nowrap">{BANK_LABELS[it.bank] || it.bank}</td>
                      <td className="p-2 max-w-[220px] truncate" title={it.description}>{it.description || '-'}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(it.amount)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${it.reason === 'no_customer_match' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {REASON_LABELS[it.reason] || it.reason}
                        </span>
                        {it.reason === 'no_account_row' && it.matched_customer_name && (
                          <div className="text-xs text-slate-400 mt-0.5">→ {it.matched_customer_name}</div>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <Select
                            value={selections[k] || it.matched_customer_id || ''}
                            onValueChange={(v) => setSelections(prev => ({ ...prev, [k]: v }))}
                          >
                            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`assign-select-${k}`}>
                              <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            className="h-8 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleAssign(it)}
                            disabled={assigning === k}
                            data-testid={`assign-btn-${k}`}
                          >
                            {assigning === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                          </Button>
                        </div>
                        {it.reason === 'no_account_row' && (
                          <div className="text-xs text-amber-600 mt-1">Needs an account row for this date first</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnmatchedDeposits;
